import {
  AtSymbolIcon, 
  NewspaperIcon, 
  PhoneIcon, 
  UserCircleIcon, 
  UsersIcon,
  VideoCameraIcon, 
} from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import { ContactInteractionType } from '../../../../@types/contactInteraction';
import type { Owner } from '../../../../@types/Owner';
import type { UnifiedContact } from '../../../../utils/contactUtils';
import { getMeetingTypeInfo, getMergedContactHistory } from '../../../../utils/contactUtils';
import AccessibleEmoji from '../../../common/AccessibleEmoji';
import { formatDateFromIsoString } from '../../utils/formatters';
import PreviousContactListPopup from './PreviousContactListPopup';

// Component for displaying the most recent contact information for an owner
interface LastContactInfoProps {
  owner: Owner;
}

const LastContactInfo: React.FC<LastContactInfoProps> = ({ owner }) => {
  const [isContactHistoryOpen, setIsContactHistoryOpen] = useState(false);
  const mergedContacts = getMergedContactHistory(owner);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const getCivilityLabel = (civility: number): string => {
    switch (civility) {
    case 1: return 'M.';
    case 2: return 'Mme';
    case 3: return 'Société';
    default: return '';
    }
  };

  const getFullName = (ownerToName: Owner): string => {
    const civility = ownerToName.civility ? getCivilityLabel(ownerToName.civility) : '';
    return `${civility} ${ownerToName.firstname || ''} ${ownerToName.lastname || ''}`.trim();
  };

  // Get meeting type emoji and label based on contact type
  // Removed local getMeetingTypeInfo function - now using it from utils

  if (mergedContacts.length === 0) {
    return <div className="text-gray-500">Aucun contact</div>;
  }
  
  const lastContact = mergedContacts[0];
  
  // Get appropriate icon and label based on contact type and source
  let contactIcon;
  let contactTypeLabel;
  let iconColorClass = 'text-gray-600';
  
  // If it's an interaction (not a regular contact), use UsersIcon with yellow color
  if (lastContact.source !== 'contact') {
    contactIcon = <UsersIcon className="mr-1 inline h-5 w-5 shrink-0 text-amber-500" />;
    contactTypeLabel = 'Interaction';
    iconColorClass = 'text-amber-500';
  } else {
    // For regular contacts, use the standard icons
    switch (lastContact.type) {
    case ContactInteractionType.MAIL:
    case ContactInteractionType.MAIL_FOR_CONSENT:
    case ContactInteractionType.MAIL_FOLLOW_UP:
      contactIcon = <AtSymbolIcon className={`h-5 w-5 ${iconColorClass} mr-1 inline shrink-0`} />;
      contactTypeLabel = lastContact.type === ContactInteractionType.MAIL_FOLLOW_UP
        ? 'Email de relance'
        : lastContact.type.includes('CONSENT') ? 'Email de consentement' : 'Email';
      break;
    case ContactInteractionType.PAPER:
    case ContactInteractionType.PAPER_FOR_CONSENT:
      contactIcon = <NewspaperIcon className={`h-5 w-5 ${iconColorClass} mr-1 inline shrink-0`} />;
      contactTypeLabel = lastContact.type.includes('CONSENT') ? 'Courrier de consentement' : 'Courrier';
      break;
    case ContactInteractionType.PHONE:
      contactIcon = <PhoneIcon className={`h-5 w-5 ${iconColorClass} mr-1 inline shrink-0`} />;
      contactTypeLabel = 'Téléphone';
      break;
    case ContactInteractionType.VISIO:
      contactIcon = <VideoCameraIcon className={`h-5 w-5 ${iconColorClass} mr-1 inline shrink-0`} />;
      contactTypeLabel = 'Visio';
      break;
    case ContactInteractionType.PHYSICAL:
      contactIcon = <UserCircleIcon className={`h-5 w-5 ${iconColorClass} mr-1 inline shrink-0`} />;
      contactTypeLabel = 'Présentiel';
      break;
    default:
      contactIcon = <PhoneIcon className={`h-5 w-5 ${iconColorClass} mr-1 inline shrink-0`} />;
      contactTypeLabel = lastContact.type.replaceAll('-', ' ');
    }
  }
  
  // Get user information if available
  const hasUserInfo = lastContact.user && lastContact.user.firstname && lastContact.user.lastname;
  const userInfo = hasUserInfo ? 
    `${lastContact.user.firstname} ${lastContact.user.lastname}` : 
    'Utilisateur inconnu';

  // Get source badge if it's not a regular contact
  const getSourceBadge = (contact: UnifiedContact) => {
    if (contact.source === 'contact') {
      return null;
    }
    
    const sourceLabels = {
      qualityReference: 'Référent mutation location',
      rentManager: 'Gestionnaire locatif',
      salesManager: 'Transactionnaire',
      lead: 'Cloture',
    };

    return (
      <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
        {sourceLabels[contact.source]}
      </span>
    );
  };
  
  return (
    <div>
      <div className="flex items-center font-medium">
        {contactIcon}
        <span>{contactTypeLabel}</span>
        {getSourceBadge(lastContact)}
      </div>
      <div className="mt-1 text-sm text-gray-600">{lastContact.createdAt ? formatDate(lastContact.createdAt) : '-'}</div>
      <div className="mt-1 flex items-center text-sm">
        <UserCircleIcon className="mr-1 h-5 w-5 shrink-0 text-gray-500" />
        <span className="text-gray-700">{userInfo}</span>
      </div>
      
      {/* Show meeting information if available */}
      {lastContact.meetingAt && (
        <div className="mt-2 text-sm text-gray-700">
          <div className="font-medium">Rendez-vous prévu :</div>
          <div className="mt-0.5 text-xs">{formatDateFromIsoString(lastContact.meetingAt)}</div>
          <div className="mt-0.5 flex items-center text-xs">
            {(() => {
              const { emoji, label } = getMeetingTypeInfo(lastContact.type);
              return (
                <>
                  <AccessibleEmoji symbol={emoji} label="meeting type icon" className="mr-1" />
                  <span>Type : {label}</span>
                </>
              );
            })()}
          </div>
        </div>
      )}
      
      {/* Show comment if available */}
      {lastContact.comment && (
        <div className="mt-2 text-sm">
          <div className="font-medium text-gray-700">Commentaire :</div>
          <div className="mt-0.5 text-xs text-gray-600">{lastContact.comment}</div>
        </div>
      )}
      
      {/* Clickable element to view contact history */}
      {mergedContacts.length > 0 && (
        <button
          onClick={() => setIsContactHistoryOpen(true)}
          className="mt-2 flex items-center text-sm text-rgpd-blue hover:underline"
        >
          <span>Voir l'historique</span>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="ml-1 h-4 w-4" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9 5l7 7-7 7" 
            />
          </svg>
        </button>
      )}

      {isContactHistoryOpen && (
        <PreviousContactListPopup
          isOpen={isContactHistoryOpen}
          onClose={() => setIsContactHistoryOpen(false)}
          mergedContacts={mergedContacts}
          ownerName={getFullName(owner)}
        />
      )}
    </div>
  );
};

export default LastContactInfo; 